import { useState, useEffect } from "react";
import useWindowSize from "../../../hooks/useWindowSize";
import { ACTION_TYPES } from "../../../constants/actionBar";

/**
 * A hook to detect overflow in the action bar and decide which icon actions should be rendered as overflowing.
 * @param {Array} actionDefs - An array of action definitions. Each action definition should have a name and a type at the very least.
 * @param {Object} ref - A ref to the action bar. It is used to get the widths of the action bar and its children and calculate the overflow.
 * @returns {Array} - An array of the names of the icon actions that should be rendered as overflowing.
 */
const useOverflowingActions = (actionDefs = [], ref = {}) => {
  const [overflowingActions, setOverflowingActions] = useState([]);
  const { width: windowWidth } = useWindowSize();

  // We get the names of the icon actions from the actionDefs array
  const iconNames = actionDefs
    .filter((actionDef) => actionDef.type === ACTION_TYPES.ICON)
    .map((actionDef) => actionDef.name);

  useEffect(() => {
    if (!ref.current) return;

    // We use the ref to get the width of the action bar
    let actionBarWidth = ref.current.offsetWidth ?? 0;

    // The title, div and actions are the three main parts of the action bar (children of the ref)
    let [title, div, actions] = ref.current.children ?? [];
    // We calculate the width of each part of the action bar
    let titleWidth = title?.offsetWidth ?? 0;
    let divWidth = div?.offsetWidth ?? 0;
    let actionsWidth = actions?.offsetWidth ?? 0;

    // We sum the widths of the title, div and actions to get the total width of the content of the bar
    let barContentWidth = titleWidth + divWidth + actionsWidth;

    // We check if the content of the bar is wider than the bar itself with a safety margin of 100px
    if (barContentWidth + 100 > actionBarWidth) {
      // The available space for actions is the width of the bar minus the sum of the widths of the title and the div
      let spaceForActions = actionBarWidth - (titleWidth + divWidth);

      // The ids of the wrappers of all action groups except for the icon group (we will use them to calculate the space they take)
      let wrapperIds = [
        "search-group-wrapper",
        "switch-group-wrapper",
        "more-group-wrapper",
        "button-group-wrapper",
        "add-on-group-wrapper",
        "custom-group-wrapper",
      ];

      // The available space for the icon group is the available space for actions minus the sum of the widths of the other action groups
      let spaceForIconGroup =
        spaceForActions -
        wrapperIds.reduce((acc, id) => {
          return acc + getElementOffsetWidthById(id);
        }, 0);

      // We check if there is not enough space for at least one icon action (38px) plus the padding left and right of the icon group (10px each)
      // TODO: it is probably a good idea to move the pixel values to constants and use them in the components
      if (spaceForIconGroup < 38 + 10 + 10) {
        // If not, we set all icon actions as overflowing and return early
        setOverflowingActions(iconNames);
        return;
      }

      let overflowingIconNames = getOverflowingIconNames(
        iconNames,
        spaceForIconGroup
      );
      setOverflowingActions(overflowingIconNames);
    } else {
      setOverflowingActions([]);
    }
  }, [windowWidth, ref.current, iconNames.join(",")]);

  return [overflowingActions];
};

export default useOverflowingActions;

const getElementOffsetWidthById = (id) => {
  let element = document.getElementById(id);
  // We need to account for the width of the more action, even if it is not rendered,
  // because we need to calculate the space it will take when it is rendered
  // (constant width of 38px because this is the width of icon actions without labels)
  // TODO: it is probably a good idea to move the pixel values to constants and use them in the components
  return element?.offsetWidth ?? (id === "more-wrapper" ? 38 : 0);
};

const getOverflowingIconNames = (iconNames, space) => {
  // We calculate the width of each icon action using their ids
  // (we cannot use a constant width because the icon actions may vary in size depending on the existence of a label)
  let iconWidths = iconNames.map((name) => {
    let iconEl = document.getElementById(`icon-${name}`);
    return iconEl?.offsetWidth ?? 0;
  });

  let iconCount = iconWidths.length;
  let sum = 10 + 10;
  // We sum the widths of the icon actions until we run out of space or icon actions,
  // starting with the padding left and right of the icon group (10px each)
  for (let i = 0; i < iconCount; i++) {
    sum += iconWidths[i];
    // We need to acount for the 15px gap between any two icon actions
    if (i !== iconCount - 1) {
      // TODO: it is probably a good idea to move the pixel values to constants and use them in the components
      sum += 15;
    }
    if (sum > space) {
      // If we run out of space, we return the first i icon action names as overflowing
      return iconNames.slice(0, i);
    }
  }
  // If we run out of icon action names, we return all of them as overflowing
  return [...iconNames];
};
